import { clsx } from 'clsx';
import { FILTER_GLOW_ID } from 'game/defs/FilterGlow';
import { FILTER_GLOW_BEIGE_ID } from 'game/defs/FilterGlowBeige';
import { PATTERN_LINES_ID } from 'game/defs/PatternLines';
import { createStyles } from 'game/lib/createStyles';
import { ModuleType, NearbySelection, determineNearbySelection } from 'game/state/BotState';
import { Message } from '../Message';
import { ModuleComponentProps } from './ModuleProps';
import { BotIcon } from './icons/BotIcon';
import { DryPlantIcon } from './icons/DryPlantIcon';
import { HumanIcon } from './icons/HumanIcon';
import { TreeIcon } from './icons/TreeIcon';
import { WaterIcon } from './icons/WaterIcon';
import { moduleStyles } from './moduleStyles';

const messages: Record<NearbySelection, string> = {
  [NearbySelection.DryPlant]: 'Unwatered plant',
  [NearbySelection.Tree]: 'Tree',
  [NearbySelection.Water]: 'Water source',
  [NearbySelection.Human]: 'Human',
  [NearbySelection.Bot]: 'Robot',
};

const styles = createStyles({
  select: {
    WebkitAppearance: 'none',
    width: '100%',
    height: '100%',
    cursor: 'pointer',
  },
});

const renderIcon = (selection: NearbySelection | null): JSX.Element | null => {
  switch (selection) {
    case NearbySelection.DryPlant: return <DryPlantIcon />;
    case NearbySelection.Tree: return <TreeIcon />;
    case NearbySelection.Water: return <WaterIcon />;
    case NearbySelection.Human: return <HumanIcon />;
    case NearbySelection.Bot: return <BotIcon />;
    case null: return null;
  }
};

interface ButtonProps {
  active: boolean,
}

const Button: React.FC<ButtonProps> = ({ active }) => (
  <g>
    {active ? (
      <>
        <rect fill="#8C8A83" stroke="#92856D" strokeWidth={2} width={80} height={42} rx={2} y={4} />
        <rect fill="#FFFFFF" stroke="#CCC5BC" strokeWidth={2} width={80} height={42} rx={2} filter={`url(#${FILTER_GLOW_BEIGE_ID})`} />
      </>
    ) : (
      <>
        <rect fill="#CCCAC2" stroke="#92856D" strokeWidth={2} width={80} height={42} rx={2} y={4} />
        <rect fill="#CCCAC2" stroke="#92856D" strokeWidth={2} width={80} height={42} rx={2} />
      </>
    )}
  </g>
);

export const NearbyModuleComponent: React.FC<ModuleComponentProps<ModuleType.Nearby>> = ({
  module,
  bot,
  grabbing,
  onGrabStart,
}) => {
  return (
    <>
      <path
        fill="#e8e2d4"
        stroke="#a08c60"
        strokeMiterlimit="10"
        d="M0.5 0.5H200.5V600.5H0.5z"
      />
      <path d="M0.5 100.5H200.5V480.5H0.5z" opacity="0.81" />
      <path d="M0.5 290.5H200.5V480.5H0.5z" />
      <path
        fill="#3a2522"
        d="M593.39 93.38a13.19 13.19 0 015.4-8.34 13.27 13.27 0 0110.05-2.18 13.73 13.73 0 014.72 1.87 13.1 13.1 0 013.68 3.48 12.83 12.83 0 012.1 4.83l2.81 13-5.15 1.07-2.82-13a7.72 7.72 0 00-1.26-2.9 8 8 0 00-8.26-3.18 7.68 7.68 0 00-2.89 1.26 8 8 0 00-2.12 2.21 8.1 8.1 0 00-1.13 2.84 7.79 7.79 0 00.05 3.15l2.81 13-5.13 1.11-2.81-13a12.8 12.8 0 01-.05-5.22zM642.18 92.07l4.41 2.84-1.4 2.17a13.07 13.07 0 01-3.59 3.74 12.88 12.88 0 01-4.74 2.05 13.05 13.05 0 01-5.17.1 13.2 13.2 0 01-4.82-1.91 12.66 12.66 0 01-3.75-3.68 13.8 13.8 0 01-2-4.67 13.54 13.54 0 01-.13-5 12.77 12.77 0 011.9-4.88 13 13 0 013.7-3.83 13.17 13.17 0 019.73-2.11 13 13 0 014.91 1.92l2.18 1.4-11.24 17.46a7.67 7.67 0 004.8-.28 7.77 7.77 0 003.81-3.15zm-14.86-6.49a7.88 7.88 0 00.43 9.25c.37-.56.91-1.41 1.65-2.56l2.35-3.64q1.25-1.93 2.4-3.69l1.79-2.74a7.92 7.92 0 00-4.81.26 7.52 7.52 0 00-3.81 3.12zM656.81 71.34a12.8 12.8 0 015.26-.08 13.31 13.31 0 018.34 5.37 12.86 12.86 0 012.1 4.82l2.81 13-12.91 2.79a12.81 12.81 0 01-5.23.08 13.38 13.38 0 01-4.75-1.89 13.58 13.58 0 01-3.7-3.55 13.38 13.38 0 01-2.18-10 13.36 13.36 0 011.91-4.7 13.57 13.57 0 013.54-3.74 12.8 12.8 0 014.81-2.1zm12.27 19.06l-1.7-7.84a7.68 7.68 0 00-1.26-2.89 8 8 0 00-5-3.25 7.79 7.79 0 00-3.15 0 8.11 8.11 0 00-5.12 3.58 8 8 0 00-1.13 2.84 8.1 8.1 0 001.28 6.03 8 8 0 008.26 3.21zM686.77 64.85l1.11 5.15-2.59.57a7.72 7.72 0 00-2.9 1.26 7.89 7.89 0 00-2.11 2.2 8.08 8.08 0 00-1.14 2.84 7.83 7.83 0 00.05 3.16L682 93l-5.18 1.12-2.81-13a12.91 12.91 0 011.82-10 13.76 13.76 0 013.55-3.67 12.94 12.94 0 014.8-2.09zM706.51 61.74a13.61 13.61 0 014.7 1.88 13.45 13.45 0 013.65 3.49 12.83 12.83 0 012.1 4.83 13 13 0 01.08 5.23 13.72 13.72 0 01-1.87 4.75 13.1 13.1 0 01-3.48 3.68 12.83 12.83 0 01-4.83 2.1 13 13 0 01-5.23.08 13.45 13.45 0 01-8.45-5.44 12.94 12.94 0 01-2.09-4.8L686 54.27l5.13-1.12 2.25 10.38 7.83-1.7a13 13 0 015.3-.09zm-.76 20.83a7.61 7.61 0 002.89-1.26 7.94 7.94 0 003.25-5.08 8.12 8.12 0 00-.06-3.18 7.72 7.72 0 00-1.26-2.9 8.07 8.07 0 00-2.21-2.15 7.93 7.93 0 00-2.84-1.14 7.79 7.79 0 00-3.15.06l-7.84 1.69 1.69 7.78a7.93 7.93 0 006.35 6.2 8.12 8.12 0 003.18-.02zM741.58 53l5 23.27a13 13 0 01.09 5.26 13.67 13.67 0 01-1.88 4.72 12.84 12.84 0 01-3.44 3.75 13 13 0 01-4.82 2.11l-2.6.56-1.11-5.13 2.59-.56a7.72 7.72 0 002.9-1.26 8.06 8.06 0 002.11-2.21 8 8 0 001.07-6v-.06a12.79 12.79 0 01-7.2 4.33 12.86 12.86 0 01-5.24.08 13.29 13.29 0 01-8.44-5.44 12.68 12.68 0 01-2.1-4.79l-2.8-12.91 5.13-1.11 2.8 12.91a8 8 0 001.27 2.92 8.07 8.07 0 002.21 2.15 8.14 8.14 0 006.06 1.07 7.86 7.86 0 005-3.47 7.81 7.81 0 001.13-2.87 8 8 0 000-3.19l-2.8-12.9z"
        transform="translate(-572.25 -35.01)"
      />
      <path d="M150.5 560.5L130.5 550.5 130.5 570.5 150.5 560.5z" />
      <text
        fontSize="27.77"
        transform="translate(52.15 530.29)"
      >
        FOUND
      </text>
      <path
        fill="#fff"
        d="M30.5 180.5L10.5 170.5 10.5 190.5 30.5 180.5z"
      />
      <text
        fill="#fff"
        fontSize="26"
        transform="translate(36.67 250.29)"
      >
        SET
      </text>
      <text
        fill="#fff"
        fontSize="27.77"
        transform="translate(10.5 130.29)"
      >
        ITEM
      </text>
      <circle cx="60.5" cy="180.02" r="19.5" fill="#61BAC9" />
      <path
        fill="#549391"
        d="M632.75 196a19 19 0 11-19 19 19 19 0 0119-19m0-1a20 20 0 1020 20 20 20 0 00-20-20z"
        transform="translate(-572.25 -35.01)"
      />
      <g fill="#fff" opacity="0.75">
        <circle cx="60.5" cy="181.02" r="9.5" />
        <path
          d="M632.75 207a9 9 0 11-9 9 9 9 0 019-9m0-1a10 10 0 1010 10 10 10 0 00-10-10z"
          transform="translate(-572.25 -35.01)"
        />
      </g>
      <circle cx="60.5" cy="180.02" r="9.5" />
      <path
        fill="#549391"
        d="M632.75 206a9 9 0 11-9 9 9 9 0 019-9m0-1a10 10 0 1010 10 10 10 0 00-10-10z"
        transform="translate(-572.25 -35.01)"
      />
      <circle cx="100.5" cy="560.02" r="19.5" fill="#ff1d25" />
      <path
        fill="#c1272d"
        d="M672.75 576a19 19 0 11-19 19 19 19 0 0119-19m0-1a20 20 0 1020 20 20 20 0 00-20-20z"
        transform="translate(-572.25 -35.01)"
      />
      <g fill="#fff" opacity="0.75">
        <circle cx="100.5" cy="561.02" r="9.5" />
        <path
          d="M672.75 587a9 9 0 11-9 9 9 9 0 019-9m0-1a10 10 0 1010 10 10 10 0 00-10-10z"
          transform="translate(-572.25 -35.01)"
        />
      </g>
      <circle cx="100.5" cy="560.02" r="9.5" />
      <path
        fill="#c1272d"
        d="M672.75 586a9 9 0 11-9 9 9 9 0 019-9m0-1a10 10 0 1010 10 10 10 0 00-10-10z"
        transform="translate(-572.25 -35.01)"
      />

      <g transform="translate(100 385)" filter={`url(#${FILTER_GLOW_ID})`}>
        {renderIcon(determineNearbySelection(module, bot.props.state))}
      </g>
      <path d="M0.5 290.5H200.5V480.5H0.5z" fill={`url(#${PATTERN_LINES_ID})`} />
      <g transform="translate(110 160)">
        <Button active={!!module.inputs.item} />
      </g>
      <g transform="translate(110 215)">
        <Button active={!module.inputs.item} />
      </g>
      <rect
        className={clsx(moduleStyles.grab, { [moduleStyles.grabbing]: grabbing })}
        width={200}
        height={600}
        onMouseDown={onGrabStart}
      />

      <foreignObject x="10" y="215" width="180" height="40" opacity="0">
        <select
          className={styles.select}
          value={module.config.manualSelection || ''}
          onChange={e => {
            // TODO: The blur() prevents key presses like WASD from changing the selection.
            // This `select` will eventually be replaced by something like a modal
            // since there'll theoretically be LOTS of things to choose from.
            e.currentTarget.blur();
            module.config.manualSelection = e.currentTarget.value as NearbySelection || null;
          }}
        >
          <option value="">
            <Message text="" />
          </option>
          {Object.values(NearbySelection).map(value => (
            <option key={value} value={value}>
              <Message text={messages[value]} />
            </option>
          ))}
        </select>
      </foreignObject>
    </>
  );
};
